import { Alert, Badge, Button, Code, CopyButton, Group, Modal, Select, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { type Schemas, api, call, hasPermission } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, Loading, formatTs, notifyError } from "../components/common";

export function GatewaysPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [opened, { open, close }] = useDisclosure();
  const [form, setForm] = useState({ factory_id: "", name: "", kind: "edge" as "edge" | "simulator" });
  const [created, setCreated] = useState<Schemas["GatewayCredentials"] | null>(null);
  const gateways = useQuery({ queryKey: ["gateways"], queryFn: () => call(api.GET("/api/v1/gateways")) });
  const factories = useQuery({ queryKey: ["factories"], queryFn: () => call(api.GET("/api/v1/factories")) });
  const create = useMutation({
    mutationFn: () => call(api.POST("/api/v1/gateways", { body: form })),
    onSuccess: (g) => { setCreated(g.credentials); void qc.invalidateQueries({ queryKey: ["gateways"] }); close(); },
    onError: notifyError,
  });
  const rotate = useMutation({
    mutationFn: (id: string) => call(api.POST("/api/v1/gateways/{gateway_id}/rotate-secret", { params: { path: { gateway_id: id } } })),
    onSuccess: (c) => setCreated(c),
    onError: notifyError,
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "disabled" }) =>
      call(api.PATCH("/api/v1/gateways/{gateway_id}", { params: { path: { gateway_id: id } }, body: { status } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["gateways"] }),
    onError: notifyError,
  });
  const canManage = hasPermission(me, "gateway:manage");

  if (gateways.isLoading) return <Loading />;
  if (gateways.error) return <ErrorAlert error={gateways.error} />;
  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Edge gateways</Title>
        {canManage && <Button onClick={open}>Register gateway</Button>}
      </Group>
      {created && (
        <Alert color="orange" title="Gateway credentials — shown only once" withCloseButton onClose={() => setCreated(null)}>
          <Stack gap={4}>
            <Text size="sm">Store the secret in the gateway configuration now. The platform keeps only a hash.</Text>
            <Text size="sm">Gateway ID: <Code>{created.gateway_id}</Code></Text>
            <Group gap="xs">
              <Text size="sm">Secret: <Code>{created.secret}</Code></Text>
              <CopyButton value={created.secret}>
                {({ copied, copy }) => <Button size="compact-xs" onClick={copy}>{copied ? "Copied" : "Copy"}</Button>}
              </CopyButton>
            </Group>
            <Text size="sm">MQTT topic prefix: <Code>{created.mqtt.topic_prefix}</Code>
              {!created.mqtt.provisioned && " (broker provisioning disabled — use HTTP ingestion)"}</Text>
          </Stack>
        </Alert>
      )}
      <Table.ScrollContainer minWidth={600}>
        <Table striped>
          <Table.Thead><Table.Tr><Table.Th>Name</Table.Th><Table.Th>Kind</Table.Th><Table.Th>Factory</Table.Th><Table.Th>Status</Table.Th><Table.Th>Last seen</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {gateways.data!.map((g) => (
              <Table.Tr key={g.id}>
                <Table.Td>{g.name}</Table.Td>
                <Table.Td><Badge variant="light" color={g.kind === "simulator" ? "gray" : "indigo"}>{g.kind}</Badge></Table.Td>
                <Table.Td>{factories.data?.find((f) => f.id === g.factory_id)?.name ?? "—"}</Table.Td>
                <Table.Td><Badge variant="dot" color={g.status === "active" ? "green" : "gray"}>{g.status}</Badge></Table.Td>
                <Table.Td>{formatTs(g.last_seen_at)}</Table.Td>
                <Table.Td>
                  {canManage && (
                    <Group gap={4} justify="end" wrap="nowrap">
                      <Button size="compact-xs" variant="default" loading={rotate.isPending && rotate.variables === g.id}
                        onClick={() => { if (window.confirm(`Issue a new secret for ${g.name}? The current secret stops working immediately.`)) rotate.mutate(g.id); }}>
                        New secret
                      </Button>
                      <Button size="compact-xs" variant="default" color={g.status === "active" ? "red" : "green"}
                        onClick={() => setStatus.mutate({ id: g.id, status: g.status === "active" ? "disabled" : "active" })}>
                        {g.status === "active" ? "Disable" : "Enable"}
                      </Button>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal opened={opened} onClose={close} title="Register gateway">
        <Stack>
          <Select label="Factory" required data={(factories.data ?? []).map((f) => ({ value: f.id, label: f.name }))}
            value={form.factory_id || null} onChange={(v) => setForm({ ...form, factory_id: v ?? "" })} />
          <TextInput label="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.currentTarget.value })} />
          <Select label="Kind" data={["edge", "simulator"]} value={form.kind}
            onChange={(v) => setForm({ ...form, kind: (v as "edge" | "simulator") ?? "edge" })} />
          <Button loading={create.isPending} disabled={!form.factory_id || !form.name} onClick={() => create.mutate()}>Create</Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
